import React, { useState } from 'react';
import { ChevronRightIcon, MailIcon, PhoneIcon, ShieldAlertIcon } from 'lucide-react';
import { MilestonePill } from './MilestonePill';
import { ClosedDealsModal } from './ClosedDealsModal';
import { closedDealsYtd, loanOfficer, loans } from '../data/portal';
import type { Page } from '../types/portal';
interface AtAGlanceProps {
  onOpenLoan: (id: string) => void;
  onNavigate: (page: Page) => void;
}
export function AtAGlance({
  onOpenLoan,
  onNavigate
}: AtAGlanceProps) {
  const [closedOpen, setClosedOpen] = useState(false);
  const inProcess = loans.filter(l => l.status === 'in-process');
  return <aside className="space-y-6" aria-label="At a glance" data-id="element-42" data-mp="a3rccm866o9u">
      <section data-id="element-43" data-mp="63qyp3swah3u">
        <h2 className="text-sm font-semibold text-slate-900" data-id="element-44" data-mp="640ho7fivo01">At a glance</h2>
        <div className="mt-3 grid grid-cols-2 gap-3" data-id="element-45" data-mp="2uvbcwfryy67o">
          <div className="animate-fade-up rounded-xl border border-slate-200 bg-white p-4 transition-shadow duration-200 hover:shadow-sm" style={{
          '--d': '0ms'
        } as React.CSSProperties} data-id="element-46" data-mp="2uwuty8lg8lai">
            <p className="text-sm text-slate-500" data-id="element-47" data-mp="2i420cfg58u0d">In process</p>
            <p className="mt-1 text-3xl font-semibold tracking-tight text-slate-900" data-id="element-48" data-mp="2n1lc98pdcup5">{inProcess.length}</p>
            <p className="mt-1 text-xs text-slate-500" data-id="element-49" data-mp="292lpy7pbwrw5">Active files</p>
          </div>
          <div className="animate-fade-up rounded-xl border border-slate-200 bg-white p-4 transition-shadow duration-200 hover:shadow-sm" style={{
          '--d': '60ms'
        } as React.CSSProperties} data-id="element-50" data-mp="2d3dx8ven4mps">
            <p className="text-sm text-slate-500" data-id="element-51" data-mp="242z9zqi48xjn">Closing this month</p>
            <p className="mt-1 text-3xl font-semibold tracking-tight text-slate-900" data-id="element-52" data-mp="244ag3wz1ahdx">2</p>
            <p className="mt-1 text-xs text-slate-500" data-id="element-53" data-mp="1pfo15wdbjrn8">1 closed · 1 scheduled</p>
          </div>
          <button onClick={() => setClosedOpen(true)} className="animate-fade-up col-span-2 flex items-center justify-between rounded-xl border border-slate-200 bg-white p-4 text-left transition-[color,background-color,border-color,box-shadow,transform] duration-200 ease-out hover:-translate-y-px hover:border-slate-300 hover:bg-slate-50 hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-accent" style={{
          '--d': '120ms'
        } as React.CSSProperties} data-id="element-54" data-mp="1udphi9jd1cv0">
            <span data-id="element-55" data-mp="3d9rindknq004">
              <span className="block text-sm text-slate-500" data-id="element-56" data-mp="39a8zam8hhwze">Closed YTD</span>
              <span className="mt-1 block text-3xl font-semibold tracking-tight text-slate-900" data-id="element-57" data-mp="3985yr89pglgf">
                {closedDealsYtd.length}
              </span>
            </span>
            <span className="inline-flex items-center gap-1 text-sm font-medium text-accent" data-id="element-58" data-mp="4wk84txkivz6">
              View deals
              <ChevronRightIcon className="h-4 w-4" data-id="element-59" data-mp="wjuhbiamot6" />
            </span>
          </button>
        </div>
      </section>

      <section className="animate-fade-up rounded-2xl border border-slate-200 bg-white" style={{
      '--d': '160ms'
    } as React.CSSProperties} data-id="element-60" data-mp="2lkobnq1dybjs">
        <div className="flex items-center justify-between px-5 pb-2 pt-4" data-id="element-61" data-mp="2gkjsttd094qe">
          <h2 className="text-sm font-semibold text-slate-900" data-id="element-62" data-mp="w42kztd2in31">In-process files</h2>
          <button onClick={() => onNavigate('files')} className="text-sm font-medium text-accent transition-[color,background-color,border-color,box-shadow,transform] duration-200 ease-out hover:text-accent-dark focus:outline-none focus-visible:underline" data-id="element-63" data-mp="112nbv26fjuet">
            View all
          </button>
        </div>
        <ul className="divide-y divide-slate-100 pb-2" data-id="element-64" data-mp="1et8tyoo280bn">
          {inProcess.map((l, i) => <li key={l.id} className="animate-fade-up" style={{
          '--d': `${200 + Math.min(i, 6) * 40}ms`
        } as React.CSSProperties} data-id="element-65" data-mp="1js26adrm1la8" data-mpi={i}>
              <button onClick={() => onOpenLoan(l.id)} className="flex w-full items-center justify-between gap-3 px-5 py-3 text-left transition-[color,background-color,border-color,box-shadow,transform] duration-200 ease-out hover:bg-slate-50 focus:outline-none focus-visible:bg-slate-50" data-id="element-66" data-mp="1oq3rb48ljuoc">
                <span className="min-w-0" data-id="element-67" data-mp="2zagptbeq8pvu">
                  <span className="block truncate text-sm font-medium text-slate-900" data-id="element-68" data-mp="2z9f2o8kfscxr">{l.buyer}</span>
                  <span className="block truncate text-xs text-slate-500" data-id="element-69" data-mp="2v8v91ez2nrgd">{l.street}</span>
                </span>
                <span className="flex shrink-0 flex-col items-end gap-1" data-id="element-70" data-mp="39738punht6v8">
                  <MilestonePill milestone={l.milestone} data-id="element-71" data-mp="34be3baoskvr0" />
                  <span className="text-xs tabular-nums text-slate-500" data-id="element-72" data-mp="2gk8p0zyi2bit">Est. {l.estCloseShort}</span>
                </span>
              </button>
            </li>)}
        </ul>
      </section>

      <section className="animate-fade-up rounded-2xl border border-slate-200 bg-white p-5" style={{
      '--d': '260ms'
    } as React.CSSProperties} data-id="element-73" data-mp="2qatmul6cnc0o">
        <h2 className="text-sm font-semibold text-slate-900" data-id="element-74" data-mp="2li0ku529mhsa">Your loan officer</h2>
        <div className="mt-4 flex items-center gap-3" data-id="element-75" data-mp="1xsppx47ljhm2">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-ink text-sm font-semibold text-white" data-id="element-76" data-mp="1suxmbcxla721">
            {loanOfficer.initials}
          </span>
          <div className="min-w-0 flex-1" data-id="element-77" data-mp="8asjsagh0wyd">
            <p className="text-sm font-semibold text-slate-900" data-id="element-78" data-mp="d3ve4kt2oafm">{loanOfficer.name}</p>
            <p className="text-xs text-slate-500" data-id="element-79" data-mp="3vhuw5g5asq10">{loanOfficer.title}</p>
          </div>
          <a href={`tel:${loanOfficer.phone}`} aria-label={`Call ${loanOfficer.name}`} className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-600 transition-[color,background-color,border-color,box-shadow,transform] duration-200 ease-out hover:border-slate-300 hover:text-slate-900 hover:-translate-y-px hover:shadow-sm active:translate-y-0" data-id="element-80" data-mp="3dsdpzjmfu79">
            <PhoneIcon className="h-4 w-4" data-id="element-81" data-mp="n0qb2z9p62rs" />
          </a>
          <a href={`mailto:${loanOfficer.email}`} aria-label={`Email ${loanOfficer.name}`} className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-600 transition-[color,background-color,border-color,box-shadow,transform] duration-200 ease-out hover:border-slate-300 hover:text-slate-900 hover:-translate-y-px hover:shadow-sm active:translate-y-0" data-id="element-82" data-mp="32sxdhbx6cr7q">
            <MailIcon className="h-4 w-4" data-id="element-83" data-mp="13l7a6m6nltzb" />
          </a>
        </div>
      </section>

      <button onClick={() => onNavigate('team')} className="animate-fade-up flex items-center gap-2 px-1 text-sm font-medium text-slate-600 transition-[color,background-color,border-color,box-shadow,transform] duration-200 ease-out hover:text-slate-900 focus:outline-none focus-visible:underline" style={{
      '--d': '300ms'
    } as React.CSSProperties} data-id="element-84" data-mp="zkx2n537qvic">
        <ShieldAlertIcon className="h-4 w-4 text-slate-400" data-id="element-85" data-mp="glj5yfe3n6o5" />
        Leadership escalation
        <ChevronRightIcon className="h-4 w-4 text-slate-400" data-id="element-86" data-mp="2f15lkwpb0s3p" />
      </button>

      <ClosedDealsModal open={closedOpen} onClose={() => setClosedOpen(false)} data-id="element-87" data-mp="2f0ckocd10wth" />
    </aside>;
}